import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Bethistorynew.css";
import { authService } from "../services/authService";

const BetHistoryPage = () => {
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [bets, setBets] = useState([]);
  const [displayedBets, setDisplayedBets] = useState([]);
  const [gameNameList, setGameNameList] = useState([]);
  
  // Filter states
  const [selectedGames, setSelectedGames] = useState(new Set());
  const [sessionSelectedGames, setSessionSelectedGames] = useState(new Set());
  const [showFilter, setShowFilter] = useState(false);
  
  const sessionOptions = ["Open", "Close"];
  
  // Refs for scrolling
  const scrollContainerRef = useRef(null);
  const tableScrollRef = useRef(null);
  const pageItemWidth = 60;

  const user = authService.getCurrentUser();

  // Fetch game list on mount
  useEffect(() => {
    fetchGameList();
    fetchFilteredBidHistory();
  }, []);

  const fetchGameList = async () => {
    try {
      const dashboard = await authService.getDashboardData();
      if (dashboard.success && dashboard.result) {
        // Extract game names from the result array
        const games = dashboard.result.map(game => ({
          gameId: game._id,
          gameName: game.game_name
        }));
        setGameNameList(games);
      }
    } catch (error) {
      console.error("Error fetching game list:", error);
    }
  };

  const getSelectedSessionValue = () => {
    if (sessionSelectedGames.size === 0) return "";
    if (sessionSelectedGames.size === 1) return Array.from(sessionSelectedGames)[0];
    return "Both";
  };

  const getSelectedGameIdList = () => {
    const ids = [];
    for (const name of selectedGames) {
      const game = gameNameList.find(g => g.gameName === name);
      if (game && game.gameId) {
        ids.push(game.gameId);
      }
    }
    return ids;
  };

  const fetchFilteredBidHistory = async () => {
    const sessionValue = getSelectedSessionValue();
    const gameIds = getSelectedGameIdList();
    await getBidHistory(page.toString(), sessionValue, gameIds);
  };

  const getBidHistory = async (pageNumber, session, gameList) => {
    try {
      setLoading(true);
      
      let gameIds = [];
      if (gameList && gameList.length > 0) {
        gameIds = gameList;
      } else {
        const dashboard = await authService.getDashboardData();
        if (dashboard.success && dashboard.result) {
          gameIds = dashboard.result.map(g => g._id);
        }
      }

      const payload = {
        app_key: "SgRTXsywVmWteEBLlYWecwgbDiHwlh",
        env_type: "Prod",
        unique_jwt_token: authService.getToken(),
        session: session || "Both",
        gameListId: gameIds,
        page: parseInt(pageNumber),
        limit: 10
      };

      console.log("Fetching bid history with payload:", payload);

      const res = await fetch(
        "https://playinbyd.com/api/api-bid-history-data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();
      console.log("Bid history API response:", data);
      
      if (data.success) {
        const totalRecords = data.total_count || 0;
        const totalPages = Math.ceil(totalRecords / 10);
        
        setTotalRecords(totalRecords);
        setTotalPageCount(totalPages);
        setBets(data.bid_data || []);
        setIsLoaded(true);
        
        // Apply filters
        applyFilters(data.bid_data || []);
        
        // Scroll to page
        scrollToPage(page - 1);
        
        // Scroll table to top
        if (tableScrollRef.current) {
          tableScrollRef.current.scrollTop = 0;
        }
      } else {
        console.log("No data found or API error");
        setIsLoaded(true); // ✅ IMPORTANT

        if (page > 1) {
          setPage(prev => prev - 1);
        }
        setBets([]);
        setDisplayedBets([]);
      }
    } catch (err) {
      console.error("Error fetching bid history:", err);
      if (page > 1) {
        setPage(prev => prev - 1);
      }
      setBets([]);
      setDisplayedBets([]);
      setIsLoaded(true); // ✅ IMPORTANT

    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (bidList) => {
    let filteredList = [...bidList];

    if (selectedGames.size > 0) {
      filteredList = filteredList.filter(bet => 
        selectedGames.has(bet.game_name)
      );
    }

    if (sessionSelectedGames.size > 0) {
      filteredList = filteredList.filter(bet => 
        bet.session && sessionSelectedGames.has(bet.session)
      );
    }

    setDisplayedBets(filteredList);
  };

  const scrollToPage = (index) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const totalWidth = totalPageCount * pageItemWidth;
    const maxAllowedWidth = containerWidth * 0.9;
    const finalWidth = totalPageCount >= 3 ? maxAllowedWidth : totalWidth;
    
    const targetOffset = (index * pageItemWidth) - (finalWidth / 2) + (pageItemWidth / 2);
    const maxScroll = Math.max(0, totalWidth - finalWidth);
    const clampedOffset = Math.max(0, Math.min(targetOffset, maxScroll));
    
    container.scrollTo({
      left: clampedOffset,
      behavior: 'smooth'
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPageCount) {
      setPage(newPage);
      fetchFilteredBidHistory();
    }
  };

  const handleFilterSubmit = () => {
    setShowFilter(false);
    setPage(1);
    fetchFilteredBidHistory();
  };

  const handleFilterCancel = () => {
    setSelectedGames(new Set());
    setSessionSelectedGames(new Set());
    setShowFilter(false);
    setPage(1);
    fetchFilteredBidHistory();
  };

  const refreshData = () => {
    fetchFilteredBidHistory();
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusMessage = (payStatus) => {
    switch (payStatus) {
      case "1": return "Congratulations";
      case "2": return "Better luck next time";
      default: return "All the best";
    }
  };

  const getStatusColor = (payStatus) => {
    switch (payStatus) {
      case "1": return "#10b981"; // Green for win
      case "2": return "#ef4444"; // Red for loss
      default: return "#3b82f6"; // Blue for pending
    }
  };

  // Calculate pagination width
  const calculatePaginationWidth = () => {
    const containerWidth = window.innerWidth;
    const totalWidth = totalPageCount * pageItemWidth;
    const maxAllowedWidth = containerWidth * 0.4;
    return totalPageCount >= 3 ? maxAllowedWidth : totalWidth;
  };

  // Toggle game selection
  const toggleGameSelection = (gameName) => {
    const newSet = new Set(selectedGames);
    if (newSet.has(gameName)) {
      newSet.delete(gameName);
    } else {
      newSet.add(gameName);
    }
    setSelectedGames(newSet);
  };

  // Toggle session selection
  const toggleSessionSelection = (session) => {
    const newSet = new Set(sessionSelectedGames);
    if (newSet.has(session)) {
      newSet.delete(session);
    } else {
      newSet.add(session);
    }
    setSessionSelectedGames(newSet);
  };

  return (
    <div className="bid-history-container">
      {/* Header */}
      <div className="bid-history-header">
        <button 
          className="back-button"
          onClick={() => window.history.back()}
        >
          ←
        </button>
        <h1 className="bid-history-title">Bid History</h1>
        <div className="header-actions">
          <button 
            className="filter-button"
            onClick={() => setShowFilter(true)}
          >
            <span className="filter-icon"><img src="./filter.png" alt="Filter" /></span>
          </button>
          <button 
            className="filter-button"
            onClick={() => {
              // Reset all filters
              setSelectedGames(new Set());
              setSessionSelectedGames(new Set());
              setPage(1);
              // Fetch data without filters
              fetchFilteredBidHistory();
            }}          >
            <span className="filter-icon"><img src="./refresh.png" alt="Refresh" /></span>
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div className="filter-modal-overlay" onClick={() => setShowFilter(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h2>FILTER TYPE</h2>
              <button 
                className="close-filter"
                onClick={() => setShowFilter(false)}
              >
                ×
              </button>
            </div>
            
            <div className="filter-content">
              <div className="filter-section">
                <h3>By Game Type</h3>
                <div className="session-filters">
                  {sessionOptions.map((session) => (
                    <div key={session} className="filter-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={`session-${session}`}
                        checked={sessionSelectedGames.has(session)}
                        onChange={() => toggleSessionSelection(session)}
                        className="filter-checkbox-input"
                      />
                      <label 
                        htmlFor={`session-${session}`}
                        className="filter-checkbox-label"
                      >
                        <span className="checkmark"></span>
                        <span className="filter-label-text">{session}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <h3>By Game</h3>
                <div className="game-filters-container">
                  <div className="game-filters">
                    {gameNameList.length > 0 ? (
                      gameNameList.map((game) => (
                        <div key={game.gameId} className="filter-checkbox-wrapper">
                          <input
                            type="checkbox"
                            id={`game-${game.gameId}`}
                            checked={selectedGames.has(game.gameName)}
                            onChange={() => toggleGameSelection(game.gameName)}
                            className="filter-checkbox-input"
                          />
                          <label 
                            htmlFor={`game-${game.gameId}`}
                            className="filter-checkbox-label"
                          >
                            <span className="checkmark"></span>
                            <span className="filter-label-text">{game.gameName}</span>
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="no-games-message">
                        Loading games...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button 
                className="set-button"
                onClick={handleFilterSubmit}
                disabled={selectedGames.size === 0 && sessionSelectedGames.size === 0}
              >
                SET
              </button>
              <button 
                className="cancel-button"
                onClick={handleFilterCancel}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bid-history-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading bid history...</p>
          </div>
        ) : !isLoaded ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Initializing...</p>
          </div>
        ) : displayedBets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Data Found</h3>
            <p>No bid history available for the selected filters.</p>
            <button 
              className="clear-filters-btn"
              onClick={handleFilterCancel}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div 
            className="bids-list-container"
            ref={tableScrollRef}
          >
            {displayedBets.map((bet, index) => (
              <div key={bet._id || index} className="bid-card">
                <div className="bid-card-header">
                  <h3>{bet.game_name} ({bet.session})</h3>
                </div>
                
                <div className="bid-card-body">
                  <div className="bid-info-row">
                    <div className="bid-info-column">
                      <span className="info-label">Game Type</span>
                      <span className="info-value">{bet.pana || bet.type || "N/A"}</span>
                    </div>
                    <div className="bid-info-column">
                      <span className="info-label">Digits</span>
                      <span className="info-value">
                        {bet.closedigits && bet.closedigits !== ""
                          ? `${bet.digits} - ${bet.closedigits}`
                          : bet.digits || "N/A"}
                      </span>
                    </div>
                    <div className="bid-info-column">
                      <span className="info-label">Points</span>
                      <span className="info-value">₹{bet.points || 0}</span>
                    </div>
                  </div>

                  <div className="bid-divider"></div>

                  <div className="bid-transaction-time">
                    Transaction Time: {formatDate(bet.bid_date)}
                  </div>

                  <div className="bid-divider"></div>

                  <div 
                    className="bid-status"
                    style={{ color: getStatusColor(bet.pay_status || bet.status) }}
                  >
                    {getStatusMessage(bet.pay_status || bet.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {isLoaded && displayedBets.length > 0 && totalPageCount > 1 && (
        <div className="bid-history-pagination">
          <div className="pagination-container">
            <button
              className={`pagination-button prev ${page === 1 ? 'disabled' : ''}`}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <span className="pagination-icon">←</span>
              <span className="pagination-text">Prev</span>
            </button>

            <div className="page-numbers-container">
              <div 
                className="page-numbers-scroll"
                ref={scrollContainerRef}
                style={{ width: calculatePaginationWidth() }}
              >
                {Array.from({ length: totalPageCount }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`page-number ${pageNum === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
            </div>

            <button
              className={`pagination-button next ${page === totalPageCount ? 'disabled' : ''}`}
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPageCount}
            >
              <span className="pagination-text">Next</span>
              <span className="pagination-icon">→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetHistoryPage;